/**
 * Life Blueprint V2.9.9 — M_INDICES · Sheet11 事业财富引擎 six indices
 * (V1 Task 3B-3B · isolated deterministic implementation).
 *
 * ────────────────────────────────────────────────────────────────────────
 * AUTHORITY
 *   Workbook:  Life_Blueprint_V2_9_9_R2.xlsx
 *   SHA-256:   6d97148d708a3dbc84bcec9bdce9facdb815891de077036c9fc118a75e86d500
 *   Sheet:     11_事业财富引擎
 *   Value cells:  B17..B22  (six numeric indices, 0..100)
 *   Status cells: C17..C22  (five-level status ladder)
 *   Evidence: evidence/analysis-3b3-indices-scope.json (Phase 0)
 *             evidence/analysis-3b3a-dependency-closure.json (Task 3B-3A)
 *
 * INPUT CONTRACT (all fields REQUIRED · fail-closed on absence)
 *   {
 *     lifeNumber:                integer 1..9   (M_NUM.Sheet02.C5)
 *     personalYear:              integer 1..9   (M_NUM.Sheet02.C9, but supplied
 *                                                by the caller — see below)
 *     referenceYear:             integer ≥ 1    (echo of caller-supplied year;
 *                                                consumed here only for
 *                                                traceability / metadata)
 *     currentGoal:               one of 12 canonical enum values (Sheet01.C14 DV)
 *     balanceIndex:              integer 0..100 (M_WX.Sheet04.B14)
 *     dayMasterStatus:           string        (BZ_五行强弱.B52)  — supported
 *                                                explicit branches:
 *                                                "偏强" · "平衡" · "偏弱"
 *                                                (anything else → else branch)
 *     boundaryRelationsAllClear: boolean        (derived from BZ_关系分析.B37;
 *                                                TRUE ⇔ B37 = 无关系 branch)
 *   }
 *
 * ARCHITECTURAL NOTES
 *   - M_INDICES does NOT recompute personalYear from birthDate; the caller
 *     supplies it. The upstream personalYear contract is:
 *         personalYear = ((day + month + referenceYear − 1) mod 9) + 1
 *     Test coverage for that upstream contract lives in a separate slice
 *     (planned adapter tests / Task 3B-3-integration). See §DevNote.
 *   - `boundaryRelationsAllClear` is treated as EXTERNAL_UNMIGRATED_B37.
 *     BZ_关系分析!B37 itself is NOT migrated in this task.
 *   - This module is NOT wired into analysisEngine.js, pipeline, UI, or
 *     productionCoreUiAdapter. Consumers of this module do not exist yet.
 *
 * DETERMINISM
 *   Same input object → byte-identical output. No wall clock. No LLM.
 *   No hidden mutation. No Date.now(). No new Date(). No system-year read.
 * ────────────────────────────────────────────────────────────────────────
 */

import {
  DB_LN_SLICE_WORKBOOK_SHA256,
  DB_LN_VLOOKUP_FALLBACK,
  dbLnLookupCol11,
  dbLnLookupCol12,
} from "@/engine/v299/m_indices_db_ln";

export const M_INDICES_ENGINE_VERSION = "V2.9.9";
export const M_INDICES_MODULE_VERSION = "M_INDICES.v1.0.0-task3b-3b";
export const M_INDICES_WORKBOOK_SHA256 = DB_LN_SLICE_WORKBOOK_SHA256;

// ── Canonical currentGoal enum (verbatim from workbook C14/C36 DV) ──
export const M_INDICES_GOAL_ENUM = Object.freeze([
  "综合分析",  // 1
  "事业发展",  // 2
  "财富增长",  // 3
  "创业发展",  // 4
  "学习提升",  // 5
  "贵人合作",  // 6
  "稳定生活",  // 7
  "个人品牌",  // 8
  "销售沟通",  // 9
  "内容创作",  // 10
  "管理领导",  // 11
  "客户关系",  // 12
]);
const GOAL_ENUM_SET = new Set(M_INDICES_GOAL_ENUM);

// ── Fail-closed catalogue (only reachable codes) ────────────────────
export const M_INDICES_ERROR_CODES = Object.freeze({
  REFERENCE_MISSING: "M_INDICES_REFERENCE_MISSING",
  INVALID_INPUT:     "M_INDICES_INVALID_INPUT",
  UNSUPPORTED_GOAL:  "M_INDICES_UNSUPPORTED_GOAL",
});

function fail(code, msg) {
  const e = new Error(`[M_INDICES·V2.9.9] ${code}: ${msg}`);
  e.code = code;
  e.moduleId = "M_INDICES";
  throw e;
}

// ── Excel ROUND(x, 0) — round-half-away-from-zero (scalar helper) ───
// Kept for completeness / documentation. NOT used by the six-index
// weighted formulas (they use `excelRoundIndexExact` below, which
// bypasses IEEE-754 midpoint drift by operating in integer domain).
function excelRound0(x) {
  return x >= 0 ? Math.floor(x + 0.5) : -Math.floor(-x + 0.5);
}

// ══════════════════════════════════════════════════════════════════════
// Exact fixed-point Excel ROUND for the M_INDICES six-index weighted
// formula domain.
//
// ── PROBLEM ──────────────────────────────────────────────────────────
// The six workbook formulas take the form:
//     raw = Σ_i ( value_i × scale_i × weight_i )
//     out = MIN(100, ROUND(raw, 0))
// where scale_i ∈ {1, .8, .85, .9} (∈ hundredths) and weight_i ∈
// {.05, .10, .15, .20, .25, .30, .35} (∈ hundredths). Excel evaluates
// these coefficients as decimals with two decimal places; the resulting
// raw sum is always a rational multiple of 1/10000. In particular,
// midpoint cases such as 54.500 exist and Excel `ROUND(54.5, 0)` = 55
// (half-away-from-zero).
//
// IEEE-754 double arithmetic cannot exactly represent 0.30, 0.85, etc.,
// so `70*0.9*0.30 + 52*0.25 + 45*0.20 + 48*0.15 + 80*0.8*0.10`
// evaluates to `54.49999999999999`, and `Math.floor(x + 0.5)` returns
// 54. This is a Workbook↔JS fidelity defect; see the V1 Task 3B-3B
// Excel ROUND Fidelity Remediation writeup.
//
// ── FIX ──────────────────────────────────────────────────────────────
// Represent each coefficient as an integer numerator over 100:
//     0.30 → 30/100
//     0.35 → 35/100
//     0.85 → 85/100
//     0.90 → 90/100  … etc.
// Because value_i is an integer under the workbook input contract
// (LN_K/L rows and IFERROR fallback = integers, dayMasterStatus /
// currentGoal / personalYear / boundaryRelationsAllClear pickers return
// integers, and balanceIndex ∈ [0,100] integer per M_WX.B14), each
// term contribution to `raw × 10000` is:
//     value_i × scaleN_i × weightN_i     (exact integer)
// The sum is an exact non-negative integer N with N/10000 = raw.
// Excel ROUND(raw, 0) for raw ≥ 0 is equivalent to:
//     floor((N + 10000/2) / 10000)   ⇔   Math.floor((N + 5000) / 10000)
// with (N + 5000) ≤ ~5,010,000 (well under 2^53), keeping the division
// itself exact under IEEE-754 for this domain.
//
// This is NOT an epsilon / tolerance hack. There is no `+ EPSILON`,
// no `+ 1e-9`, and no `Number.EPSILON`. The arithmetic is bit-exact
// for every midpoint case the workbook can generate under the input
// contract, including 38.500, 54.500, 68.500 and 73.500.
// ══════════════════════════════════════════════════════════════════════
const M_INDICES_FP_SCALE = 10000; // scaleN denominator (100) × weightN denominator (100)
const M_INDICES_FP_HALF  = 5000;  // M_INDICES_FP_SCALE / 2

/**
 * Compute MIN(100, ROUND(Σ value×scale×weight, 0)) exactly.
 *
 * @param {Array<{ value: number, scaleN: number, weightN: number }>} terms
 *   Each term: `value` is an integer (or 0..100 numeric per input
 *   contract), `scaleN` is the scale numerator (denominator 100),
 *   `weightN` is the weight numerator (denominator 100).
 * @returns {number} clamped, rounded integer in [0, 100].
 */
function excelRoundIndexExact(terms) {
  let integerSum = 0;
  for (const t of terms) {
    integerSum += t.value * t.scaleN * t.weightN;
  }
  // Non-negative domain guarantee (all inputs and coefficients are non-negative).
  const rounded = Math.floor((integerSum + M_INDICES_FP_HALF) / M_INDICES_FP_SCALE);
  return Math.min(100, rounded);
}

// ══════════════════════════════════════════════════════════════════════
// Per-index lookup tables (VERBATIM extracted from workbook formulas —
// see evidence JSON). Order in Object literals is display-only; the
// pickers below use explicit string equality (mirrors Excel `=`).
// ══════════════════════════════════════════════════════════════════════

// dayMasterStatus per index (B17..B22)
const PILLAR_S = Object.freeze({
  B17: Object.freeze({ 偏强: 85, 平衡: 75, 偏弱: 60, __else__: 65 }),
  B18: Object.freeze({ 偏强: 80, 平衡: 78, 偏弱: 62, __else__: 65 }),
  B19: Object.freeze({ 偏强: 90, 平衡: 75, 偏弱: 52, __else__: 65 }),
  B20: Object.freeze({ 偏弱: 82, 平衡: 75, 偏强: 65, __else__: 70 }),
  B21: Object.freeze({ 平衡: 82, 偏强: 72, 偏弱: 70, __else__: 72 }),
  B22: Object.freeze({ 偏弱: 85, 平衡: 80, 偏强: 60, __else__: 70 }),
});
function pickPillarS(idx, s) {
  const tbl = PILLAR_S[idx];
  return Object.prototype.hasOwnProperty.call(tbl, s) ? tbl[s] : tbl.__else__;
}

// currentGoal per index (B17..B22) — 8 explicit + ELSE per formula
const GOAL_TBL = Object.freeze({
  B17: Object.freeze({ 事业发展: 90, 管理领导: 85, 创业发展: 80, 内容创作: 75, 财富增长: 70, 贵人合作: 65, 学习提升: 60, 稳定生活: 55, __else__: 70 }),
  B18: Object.freeze({ 财富增长: 90, 创业发展: 85, 事业发展: 78, 管理领导: 75, 内容创作: 70, 贵人合作: 65, 学习提升: 60, 稳定生活: 72, __else__: 70 }),
  B19: Object.freeze({ 创业发展: 92, 内容创作: 82, 财富增长: 80, 事业发展: 75, 贵人合作: 68, 管理领导: 70, 学习提升: 60, 稳定生活: 45, __else__: 70 }),
  B20: Object.freeze({ 学习提升: 92, 内容创作: 80, 创业发展: 72, 事业发展: 70, 贵人合作: 65, 财富增长: 62, 管理领导: 68, 稳定生活: 60, __else__: 70 }),
  B21: Object.freeze({ 贵人合作: 92, 内容创作: 80, 事业发展: 75, 财富增长: 70, 创业发展: 78, 管理领导: 72, 学习提升: 65, 稳定生活: 60, __else__: 70 }),
  B22: Object.freeze({ 稳定生活: 92, 财富增长: 75, 学习提升: 72, 管理领导: 70, 事业发展: 68, 贵人合作: 65, 内容创作: 60, 创业发展: 50, __else__: 70 }),
});
function pickGoal(idx, g) {
  const tbl = GOAL_TBL[idx];
  return Object.prototype.hasOwnProperty.call(tbl, g) ? tbl[g] : tbl.__else__;
}

// personalYear per index (B17..B22) · keys are integers 1..9; else 70
const PY_TBL = Object.freeze({
  B17: Object.freeze({ 8: 92, 1: 80, 5: 75, 3: 72, 2: 65, 6: 65, 9: 60, 4: 60, 7: 55, __else__: 70 }),
  B18: Object.freeze({ 8: 92, 5: 78, 1: 75, 3: 70, 4: 65, 2: 65, 6: 68, 9: 58, 7: 55, __else__: 70 }),
  B19: Object.freeze({ 1: 85, 5: 90, 8: 82, 3: 75, 9: 55, 2: 68, 6: 62, 4: 55, 7: 48, __else__: 70 }),
  B20: Object.freeze({ 7: 92, 3: 85, 4: 78, 2: 72, 6: 68, 1: 70, 5: 65, 8: 65, 9: 60, __else__: 70 }),
  B21: Object.freeze({ 2: 92, 6: 80, 3: 78, 8: 72, 5: 68, 1: 65, 4: 62, 9: 60, 7: 55, __else__: 70 }),
  B22: Object.freeze({ 4: 90, 6: 82, 7: 78, 2: 75, 8: 70, 1: 65, 9: 68, 3: 62, 5: 55, __else__: 70 }),
});
function pickPY(idx, py) {
  const tbl = PY_TBL[idx];
  return Object.prototype.hasOwnProperty.call(tbl, py) ? tbl[py] : tbl.__else__;
}

// B21 relation tail — `IF(BZ_关系分析!B37 = "本命盘四柱之间未检测到明显合冲刑害关系（仅供文化参考）", 75, 70)`
function pickRelB21(allClear) {
  return allClear ? 75 : 70;
}

// ── Status ladder (C17..C22, identical for all six) ────────────────
export const M_INDICES_STATUS_LABELS = Object.freeze({
  s85: "非常适合",
  s70: "适合发展",
  s55: "可尝试",
  s40: "谨慎推进",
  s00: "暂不建议",
});
function classifyStatus(v) {
  if (v >= 85) return M_INDICES_STATUS_LABELS.s85;
  if (v >= 70) return M_INDICES_STATUS_LABELS.s70;
  if (v >= 55) return M_INDICES_STATUS_LABELS.s55;
  if (v >= 40) return M_INDICES_STATUS_LABELS.s40;
  return M_INDICES_STATUS_LABELS.s00;
}

// ── VLOOKUP wrappers with IFERROR + per-index scale (verbatim) ─────
// The Excel formulas apply the scale INSIDE the IFERROR argument, e.g.:
//   IFERROR( VLOOKUP(...) * 0.85, 65 )
// meaning: if VLOOKUP fails, the fallback is the literal 65
// (UNSCALED). If it succeeds, the returned value is (row × scale).
//
// To preserve this contract in exact integer domain, each wrapper
// returns a term fragment `{ value, scaleN }`:
//   - Success:  { value: rawRow, scaleN: <caller-supplied scaleN> }
//   - Fallback: { value: 65,     scaleN: 100 }   (i.e. scale = 1.0)
// The formula-site call then attaches the `weightN` and consumes the
// full term through `excelRoundIndexExact`.
//
// This replaces the previous `raw * scale` (float) formulation with a
// pure integer term description; no arithmetic is performed here.
function lnKTerm(lifeNumber, scaleN) {
  if (!Number.isInteger(lifeNumber)) {
    return { value: DB_LN_VLOOKUP_FALLBACK, scaleN: 100 };
  }
  const raw = dbLnLookupCol11(lifeNumber);
  if (raw === DB_LN_VLOOKUP_FALLBACK && !(lifeNumber >= 1 && lifeNumber <= 9)) {
    return { value: DB_LN_VLOOKUP_FALLBACK, scaleN: 100 };
  }
  return { value: raw, scaleN };
}
function lnLTerm(lifeNumber, scaleN) {
  if (!Number.isInteger(lifeNumber)) {
    return { value: DB_LN_VLOOKUP_FALLBACK, scaleN: 100 };
  }
  const raw = dbLnLookupCol12(lifeNumber);
  if (raw === DB_LN_VLOOKUP_FALLBACK && !(lifeNumber >= 1 && lifeNumber <= 9)) {
    return { value: DB_LN_VLOOKUP_FALLBACK, scaleN: 100 };
  }
  return { value: raw, scaleN };
}
// BAL × scale, IFERROR default 65 (Excel `IFERROR('04'!B14 * s, 65)`).
// Under the validated input contract balanceIndex is a finite number,
// so the fallback branch is only reached defensively.
function balTerm(bal, scaleN) {
  if (!Number.isFinite(bal)) {
    return { value: DB_LN_VLOOKUP_FALLBACK, scaleN: 100 };
  }
  return { value: bal, scaleN };
}

// ══════════════════════════════════════════════════════════════════════
// Six index formulas — VERBATIM weight structure per §Phase 2 evidence.
// Each term retains the workbook (value, scale, weight) decomposition
// so the formula reads 1:1 against `evidence/analysis-3b3a-dependency-
// closure.json`. Integer scaling (denominator 100 for both scale and
// weight) is contained inside `excelRoundIndexExact`.
// ══════════════════════════════════════════════════════════════════════

function computeB17_事业适配({ lifeNumber, dayMasterStatus, currentGoal, personalYear }) {
  // Workbook: LN_K × 1 × .30 + Pillar × .35 + Goal × .20 + PY × .10 + 65 × .05
  return excelRoundIndexExact([
    { ...lnKTerm(lifeNumber, 100),                        weightN: 30 },
    { value: pickPillarS("B17", dayMasterStatus), scaleN: 100, weightN: 35 },
    { value: pickGoal("B17", currentGoal),        scaleN: 100, weightN: 20 },
    { value: pickPY("B17", personalYear),         scaleN: 100, weightN: 10 },
    { value: 65,                                  scaleN: 100, weightN:  5 },
  ]);
}

function computeB18_财富发展({ lifeNumber, dayMasterStatus, currentGoal, personalYear, balanceIndex }) {
  // Workbook: LN_L × 1 × .25 + Pillar × .35 + Goal × .25 + PY × .10 + (BAL × .8) × .05
  return excelRoundIndexExact([
    { ...lnLTerm(lifeNumber, 100),                        weightN: 25 },
    { value: pickPillarS("B18", dayMasterStatus), scaleN: 100, weightN: 35 },
    { value: pickGoal("B18", currentGoal),        scaleN: 100, weightN: 25 },
    { value: pickPY("B18", personalYear),         scaleN: 100, weightN: 10 },
    { ...balTerm(balanceIndex, 80),                       weightN:  5 },
  ]);
}

function computeB19_创业适配({ lifeNumber, dayMasterStatus, currentGoal, personalYear, balanceIndex }) {
  // Workbook: (LN_K × .9) × .30 + Pillar × .25 + Goal × .20 + PY × .15 + (BAL × .8) × .10
  return excelRoundIndexExact([
    { ...lnKTerm(lifeNumber, 90),                         weightN: 30 },
    { value: pickPillarS("B19", dayMasterStatus), scaleN: 100, weightN: 25 },
    { value: pickGoal("B19", currentGoal),        scaleN: 100, weightN: 20 },
    { value: pickPY("B19", personalYear),         scaleN: 100, weightN: 15 },
    { ...balTerm(balanceIndex, 80),                       weightN: 10 },
  ]);
}

function computeB20_学习提升({ lifeNumber, dayMasterStatus, currentGoal, personalYear }) {
  // Workbook: (LN_K × .85) × .35 + Pillar × .25 + Goal × .20 + PY × .20
  return excelRoundIndexExact([
    { ...lnKTerm(lifeNumber, 85),                         weightN: 35 },
    { value: pickPillarS("B20", dayMasterStatus), scaleN: 100, weightN: 25 },
    { value: pickGoal("B20", currentGoal),        scaleN: 100, weightN: 20 },
    { value: pickPY("B20", personalYear),         scaleN: 100, weightN: 20 },
  ]);
}

function computeB21_贵人合作({ lifeNumber, dayMasterStatus, currentGoal, personalYear, boundaryRelationsAllClear }) {
  // Workbook: (LN_K × .85) × .25 + Pillar × .25 + Goal × .15 + PY × .15 + REL × .20
  return excelRoundIndexExact([
    { ...lnKTerm(lifeNumber, 85),                         weightN: 25 },
    { value: pickPillarS("B21", dayMasterStatus), scaleN: 100, weightN: 25 },
    { value: pickGoal("B21", currentGoal),        scaleN: 100, weightN: 15 },
    { value: pickPY("B21", personalYear),         scaleN: 100, weightN: 15 },
    { value: pickRelB21(boundaryRelationsAllClear), scaleN: 100, weightN: 20 },
  ]);
}

function computeB22_稳定生活({ lifeNumber, dayMasterStatus, currentGoal, personalYear, balanceIndex }) {
  // Workbook: (BAL × .9) × .35 + Pillar × .25 + (LN_L × .85) × .20 + PY × .10 + Goal × .10
  return excelRoundIndexExact([
    { ...balTerm(balanceIndex, 90),                       weightN: 35 },
    { value: pickPillarS("B22", dayMasterStatus), scaleN: 100, weightN: 25 },
    { ...lnLTerm(lifeNumber, 85),                         weightN: 20 },
    { value: pickPY("B22", personalYear),         scaleN: 100, weightN: 10 },
    { value: pickGoal("B22", currentGoal),        scaleN: 100, weightN: 10 },
  ]);
}

// ── Input validation ───────────────────────────────────────────────
function requireInt(v, name, opts = {}) {
  if (v === undefined || v === null) {
    fail(M_INDICES_ERROR_CODES.REFERENCE_MISSING, `input.${name} is required`);
  }
  if (!Number.isInteger(v)) {
    fail(M_INDICES_ERROR_CODES.INVALID_INPUT, `input.${name} must be an integer (got ${typeof v})`);
  }
  if (typeof opts.min === "number" && v < opts.min) {
    fail(M_INDICES_ERROR_CODES.INVALID_INPUT, `input.${name} < ${opts.min}`);
  }
  if (typeof opts.max === "number" && v > opts.max) {
    fail(M_INDICES_ERROR_CODES.INVALID_INPUT, `input.${name} > ${opts.max}`);
  }
}
function requireBool(v, name) {
  if (v === undefined || v === null) {
    fail(M_INDICES_ERROR_CODES.REFERENCE_MISSING, `input.${name} is required`);
  }
  if (typeof v !== "boolean") {
    fail(M_INDICES_ERROR_CODES.INVALID_INPUT, `input.${name} must be boolean (got ${typeof v})`);
  }
}
function requireStringExact(v, name) {
  if (v === undefined || v === null) {
    fail(M_INDICES_ERROR_CODES.REFERENCE_MISSING, `input.${name} is required`);
  }
  if (typeof v !== "string" || v.length === 0) {
    fail(M_INDICES_ERROR_CODES.INVALID_INPUT, `input.${name} must be a non-empty string`);
  }
}

/**
 * Run the M_INDICES (Sheet11 six-index + status ladder) deterministic
 * slice. See "INPUT CONTRACT" JSDoc above for the required fields.
 *
 * @returns {{
 *   indices: {
 *     careerFit: number,   // B17 事业适配指数
 *     wealth:    number,   // B18 财富发展指数
 *     venture:   number,   // B19 创业适配指数
 *     learning:  number,   // B20 学习提升指数
 *     network:   number,   // B21 贵人合作指数
 *     stability: number,   // B22 稳定生活指数
 *   },
 *   statuses: { careerFit, wealth, venture, learning, network, stability },
 *   inputs: { ...echo of validated inputs },
 *   metadata: {
 *     module, engineVersion, moduleVersion, workbookHash,
 *     workbookSheet, sourceCells, deterministic,
 *     referenceYear, currentGoal,
 *     relationDependency, relationDependencyMigrated,
 *     relationDependencySource,
 *     analysisEngineConnected, uiConnected, productionReady,
 *   }
 * }}
 */
export function runMIndicesSheet11V299(input) {
  if (input === null || input === undefined || typeof input !== "object") {
    fail(M_INDICES_ERROR_CODES.REFERENCE_MISSING, "input object is required");
  }

  requireInt(input.lifeNumber, "lifeNumber");     // 1..9 in domain, but
                                                   // out-of-range routes
                                                   // to VLOOKUP IFERROR
  requireInt(input.personalYear, "personalYear"); // 1..9 in domain; else
                                                   // routes to __else__
  requireInt(input.referenceYear, "referenceYear", { min: 1 });
  requireStringExact(input.currentGoal, "currentGoal");
  if (!GOAL_ENUM_SET.has(input.currentGoal)) {
    fail(
      M_INDICES_ERROR_CODES.UNSUPPORTED_GOAL,
      `currentGoal '${input.currentGoal}' is not one of the 12 canonical values`
    );
  }
  // ── balanceIndex input contract (V1 Task 3B-3B Input-Contract Rem.) ──
  //
  //  Documented contract:  balanceIndex ∈ ℤ ∩ [0, 100]  (M_WX.Sheet04.B14 is
  //  always an integer). Runtime enforcement mirrors that contract exactly:
  //  fractional values (e.g. 80.001, 80.0001, 50.5), out-of-range values
  //  (-1, 101), and non-numeric values (NaN, Infinity, string, boolean, …)
  //  ALL fail-closed with `M_INDICES_INVALID_INPUT`. `null` / `undefined`
  //  route to `REFERENCE_MISSING` via the standard missing-field branch.
  //
  //  This is the ONLY place fixed-point integer-sum invariance can be
  //  broken from caller input — LN_K/L rows are integer-cached in
  //  `m_indices_db_ln.js`, pickers return literal integer table values,
  //  and the 65 literal is exact. Requiring `Number.isInteger` here
  //  closes the exact-arithmetic proof over the entire input domain.
  //
  //  No coercion, no clamp, no `Math.round`.
  requireInt(input.balanceIndex, "balanceIndex", { min: 0, max: 100 });
  requireStringExact(input.dayMasterStatus, "dayMasterStatus");
  requireBool(input.boundaryRelationsAllClear, "boundaryRelationsAllClear");

  const inputs = Object.freeze({
    lifeNumber: input.lifeNumber,
    personalYear: input.personalYear,
    referenceYear: input.referenceYear,
    currentGoal: input.currentGoal,
    balanceIndex: input.balanceIndex,
    dayMasterStatus: input.dayMasterStatus,
    boundaryRelationsAllClear: input.boundaryRelationsAllClear,
  });

  const b17 = computeB17_事业适配(inputs);
  const b18 = computeB18_财富发展(inputs);
  const b19 = computeB19_创业适配(inputs);
  const b20 = computeB20_学习提升(inputs);
  const b21 = computeB21_贵人合作(inputs);
  const b22 = computeB22_稳定生活(inputs);

  return Object.freeze({
    indices: Object.freeze({
      careerFit: b17,
      wealth:    b18,
      venture:   b19,
      learning:  b20,
      network:   b21,
      stability: b22,
    }),
    statuses: Object.freeze({
      careerFit: classifyStatus(b17),
      wealth:    classifyStatus(b18),
      venture:   classifyStatus(b19),
      learning:  classifyStatus(b20),
      network:   classifyStatus(b21),
      stability: classifyStatus(b22),
    }),
    inputs,
    metadata: Object.freeze({
      module: "M_INDICES",
      engineVersion: M_INDICES_ENGINE_VERSION,
      moduleVersion: M_INDICES_MODULE_VERSION,
      workbookHash: M_INDICES_WORKBOOK_SHA256,
      workbookSheet: "11_事业财富引擎",
      sourceCells: Object.freeze(["B17", "B18", "B19", "B20", "B21", "B22", "C17", "C18", "C19", "C20", "C21", "C22"]),
      deterministic: true,
      referenceYear: inputs.referenceYear,
      currentGoal: inputs.currentGoal,
      relationDependency: "BZ_关系分析!B37",
      relationDependencyMigrated: false,
      relationDependencySource: "EXTERNAL_UNMIGRATED_B37",
      analysisEngineConnected: false,
      uiConnected: false,
      productionReady: false,
    }),
  });
}
